import React, { useState, useEffect, useCallback } from 'react';
import { eventsApi } from '../../_lib/api-client';
import type { AdditionalDetail } from '../../_graphql/generated/types';

interface EventPageDesignerProps {
  eventId: string;
}

// Define the structure for page sections
const PAGE_SECTIONS = {
  INFO_CARDS: {
    key: '_section_info_cards',
    title: 'Information Cards',
    description: 'Display key event details in card format',
    settings: [
      { key: 'info_cards_visible', label: 'Show Information Cards', type: 'boolean', default: true },
      { key: 'info_cards_columns', label: 'Number of Columns', type: 'select', options: ['2', '3', '4'], default: '3' }
    ],
    contentKeys: ['Schedule', 'Venue', 'Gift Registry', 'Dress Code', 'Parking Info', 'Transportation']
  },
  FAQ: {
    key: '_section_faq',
    title: 'Frequently Asked Questions',
    description: 'Help guests with common questions',
    settings: [
      { key: 'faq_visible', label: 'Show FAQ Section', type: 'boolean', default: true },
      { key: 'faq_title', label: 'Section Title', type: 'text', default: 'Frequently Asked Questions' }
    ],
    contentKeys: ['FAQ_Q1', 'FAQ_A1', 'FAQ_Q2', 'FAQ_A2', 'FAQ_Q3', 'FAQ_A3', 'FAQ_Q4', 'FAQ_A4', 'FAQ_Q5', 'FAQ_A5']
  },
  CONTACT: {
    key: '_section_contact',
    title: 'Contact Information',
    description: 'How guests can reach you',
    settings: [
      { key: 'contact_visible', label: 'Show Contact Section', type: 'boolean', default: true },
      { key: 'contact_title', label: 'Section Title', type: 'text', default: 'Questions?' }
    ],
    contentKeys: ['Contact_Email', 'Contact_Phone', 'Contact_Message']
  },
  ACCOMMODATION: {
    key: '_section_accommodation',
    title: 'Accommodation',
    description: 'Hotel and lodging information',
    settings: [
      { key: 'accommodation_visible', label: 'Show Accommodation Section', type: 'boolean', default: false },
      { key: 'accommodation_title', label: 'Section Title', type: 'text', default: 'Where to Stay' }
    ],
    contentKeys: ['Hotel_1_Name', 'Hotel_1_Details', 'Hotel_2_Name', 'Hotel_2_Details', 'Accommodation_Notes']
  },
  GALLERY: {
    key: '_section_gallery',
    title: 'Photo Gallery',
    description: 'Share engagement photos or venue images',
    settings: [
      { key: 'gallery_visible', label: 'Show Gallery Section', type: 'boolean', default: false },
      { key: 'gallery_title', label: 'Section Title', type: 'text', default: 'Our Story' }
    ],
    contentKeys: ['Gallery_URL_1', 'Gallery_URL_2', 'Gallery_URL_3', 'Gallery_Caption']
  },
  EMAIL_SUBSCRIPTION: {
    key: '_section_email',
    title: 'Email Updates',
    description: 'Allow guests to subscribe for updates',
    settings: [
      { key: 'email_subscription_visible', label: 'Show Email Subscription', type: 'boolean', default: true }
    ],
    contentKeys: []
  }
};

interface SectionData {
  [key: string]: string | boolean;
}

const EventPageDesigner: React.FC<EventPageDesignerProps> = ({ eventId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState<{ [key: string]: AdditionalDetail }>({});
  const [localValues, setLocalValues] = useState<{ [key: string]: string }>({});
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['INFO_CARDS']));
  const [previewMode, setPreviewMode] = useState(false);
  const [saveTimeouts, setSaveTimeouts] = useState<{ [key: string]: NodeJS.Timeout }>({});

  useEffect(() => {
    fetchDetails();
  }, [eventId]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(saveTimeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, [saveTimeouts]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      console.log('EventPageDesigner: Fetching details for eventId:', eventId);
      const response = await eventsApi.getAdditionalDetails(eventId);
      console.log('EventPageDesigner: Response:', response);
      const detailsMap: { [key: string]: AdditionalDetail } = {};
      response.additionalDetails.forEach(detail => {
        detailsMap[detail.key] = detail;
      });
      setDetails(detailsMap);
      console.log('EventPageDesigner: Details count:', response.additionalDetails.length);
    } catch (err) {
      console.error('EventPageDesigner: Error fetching details:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionKey: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionKey)) {
      newExpanded.delete(sectionKey);
    } else {
      newExpanded.add(sectionKey);
    }
    setExpandedSections(newExpanded);
  };

  const saveDetail = async (key: string, value: string, displayOnPage: boolean = true) => {
    try {
      setSaving(true);
      
      if (details[key]) {
        // Update existing
        await eventsApi.updateAdditionalDetail({
          eventId,
          key,
          value,
          displayOnPage
        });
      } else {
        // Add new
        await eventsApi.addAdditionalDetail({
          eventId,
          key,
          value,
          displayOnPage
        });
      }
      
      // Update local state
      setDetails(prev => ({
        ...prev,
        [key]: { key, value, displayOnPage }
      }));
    } catch (err) {
      console.error('Error saving detail:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateDetail = useCallback((key: string, value: string, displayOnPage: boolean = true) => {
    // Update local state immediately
    setLocalValues(prev => ({ ...prev, [key]: value }));
    
    // Clear existing timeout for this key
    if (saveTimeouts[key]) {
      clearTimeout(saveTimeouts[key]);
    }
    
    // Set new timeout to save after 500ms of no changes
    const newTimeout = setTimeout(() => {
      saveDetail(key, value, displayOnPage);
      setSaveTimeouts(prev => {
        const newTimeouts = { ...prev };
        delete newTimeouts[key];
        return newTimeouts;
      });
    }, 500);
    
    setSaveTimeouts(prev => ({ ...prev, [key]: newTimeout }));
  }, [saveTimeouts, eventId, details]);

  const deleteDetail = async (key: string) => {
    try {
      setSaving(true);
      await eventsApi.deleteAdditionalDetail({ eventId, key });
      
      const newDetails = { ...details };
      delete newDetails[key];
      setDetails(newDetails);
    } catch (err) {
      console.error('Error deleting detail:', err);
    } finally {
      setSaving(false);
    }
  };

  const getDetailValue = (key: string, defaultValue: string = ''): string => {
    // Return local value if it exists (user is typing), otherwise return saved value
    return localValues[key] !== undefined ? localValues[key] : (details[key]?.value || defaultValue);
  };

  const getBooleanValue = (key: string, defaultValue: boolean = false): boolean => {
    const value = details[key]?.value;
    if (!value) return defaultValue;
    return value.toLowerCase() === 'true';
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-4"></div>
        <div className="text-gray-600">Loading page designer...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Event Web Page Details</h2>
          <p className="text-sm text-gray-600 mt-1">
            Customize how your event page appears to guests
            {saving && (
              <span className="ml-2 text-blue-600">
                <span className="animate-pulse">●</span> Saving...
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => setPreviewMode(!previewMode)}
          className="px-4 py-2 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors text-sm font-medium"
        >
          {previewMode ? 'Exit Preview' : 'Preview'}
        </button>
      </div>

      {previewMode ? (
        <div className="bg-gray-100 rounded-lg p-4">
          <iframe
            src={`/event/${eventId}/preview`}
            className="w-full h-[800px] bg-white rounded-lg shadow-lg"
            title="Event Page Preview"
          />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(PAGE_SECTIONS).map(([sectionId, section]) => {
            const isExpanded = expandedSections.has(sectionId);
            const isVisible = section.settings.find(s => s.key.endsWith('_visible'))
              ? getBooleanValue(section.settings.find(s => s.key.endsWith('_visible'))!.key, true)
              : true;

            return (
              <div
                key={sectionId}
                className={`bg-white rounded-lg shadow-sm border ${
                  isVisible ? 'border-gray-200' : 'border-gray-200 opacity-60'
                }`}
              >
                {/* Section Header */}
                <div
                  className="px-6 py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleSection(sectionId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <svg
                        className={`w-5 h-5 text-gray-400 transform transition-transform ${
                          isExpanded ? 'rotate-90' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <div>
                        <h3 className="font-medium text-gray-900">{section.title}</h3>
                        <p className="text-sm text-gray-500">{section.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {section.settings.find(s => s.key.endsWith('_visible')) && (
                        <label className="flex items-center gap-2 mr-4">
                          <span className="text-sm text-gray-600">Visible</span>
                          <input
                            type="checkbox"
                            checked={isVisible}
                            onChange={(e) => {
                              e.stopPropagation();
                              const visibleKey = section.settings.find(s => s.key.endsWith('_visible'))!.key;
                              updateDetail(visibleKey, e.target.checked.toString(), false);
                            }}
                            className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section Content - Expandable */}
                {isExpanded && (
                  <div className="p-6 space-y-6">
                    {/* Section Settings */}
                    {section.settings.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-gray-700">Settings</h4>
                        {section.settings.map(setting => {
                          if (setting.key.endsWith('_visible')) return null; // Already shown in header
                          
                          return (
                            <div key={setting.key} className="flex items-center gap-4">
                              <label className="text-sm text-gray-600 w-40">{setting.label}:</label>
                              
                              {setting.type === 'text' && (
                                <input
                                  type="text"
                                  value={getDetailValue(setting.key, setting.default as string)}
                                  onChange={(e) => updateDetail(setting.key, e.target.value, false)}
                                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                                />
                              )}
                              
                              {setting.type === 'select' && (
                                <select
                                  value={getDetailValue(setting.key, setting.default as string)}
                                  onChange={(e) => updateDetail(setting.key, e.target.value, false)}
                                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                                >
                                  {setting.options?.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Section Content Items */}
                    {section.contentKeys.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-gray-700">Content</h4>
                        
                        {sectionId === 'INFO_CARDS' && (
                          <InfoCardsEditor
                            contentKeys={section.contentKeys}
                            details={details}
                            onUpdate={updateDetail}
                            onDelete={deleteDetail}
                            getDetailValue={getDetailValue}
                          />
                        )}
                        
                        {sectionId === 'FAQ' && (
                          <FAQEditor
                            details={details}
                            onUpdate={updateDetail}
                            getDetailValue={getDetailValue}
                          />
                        )}
                        
                        {sectionId === 'CONTACT' && (
                          <ContactEditor
                            details={details}
                            onUpdate={updateDetail}
                            getDetailValue={getDetailValue}
                          />
                        )}
                        
                        {sectionId === 'ACCOMMODATION' && (
                          <AccommodationEditor
                            details={details}
                            onUpdate={updateDetail}
                            getDetailValue={getDetailValue}
                          />
                        )}
                        
                        {sectionId === 'GALLERY' && (
                          <GalleryEditor
                            details={details}
                            onUpdate={updateDetail}
                            getDetailValue={getDetailValue}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Sub-components for each section editor
const InfoCardsEditor: React.FC<{
  contentKeys: string[];
  details: { [key: string]: AdditionalDetail };
  onUpdate: (key: string, value: string, displayOnPage?: boolean) => void;
  onDelete: (key: string) => void;
  getDetailValue: (key: string, defaultValue?: string) => string;
}> = ({ contentKeys, details, onUpdate, onDelete, getDetailValue }) => {
  const [customCards, setCustomCards] = useState<string[]>([]);

  // Find existing custom cards
  useEffect(() => {
    const customKeys = Object.keys(details).filter(key => 
      !contentKeys.includes(key) && 
      !key.startsWith('_') && 
      details[key].displayOnPage
    );
    setCustomCards(customKeys);
  }, [details, contentKeys]);

  return (
    <div className="space-y-4">
      {[...contentKeys, ...customCards].map(key => {
        const detail = details[key];
        const hasContent = detail && detail.value.trim() !== '';
        
        return (
          <div key={key} className={`border ${hasContent ? 'border-green-200 bg-green-50' : 'border-gray-200'} rounded-lg p-4`}>
            <div className="flex items-start justify-between mb-2">
              <h5 className="font-medium text-gray-900">{key}</h5>
              <div className="flex items-center gap-2">
                {hasContent && (
                  <span className="text-xs text-green-600 font-medium">Active</span>
                )}
                {customCards.includes(key) && (
                  <button
                    onClick={() => onDelete(key)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <textarea
              value={getDetailValue(key)}
              onChange={(e) => onUpdate(key, e.target.value, true)}
              placeholder={`Enter content for ${key}...`}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 resize-none"
            />
          </div>
        );
      })}
      
      <button
        onClick={() => {
          const newKey = prompt('Enter a name for the new info card:');
          if (newKey && !details[newKey]) {
            onUpdate(newKey, '', true);
            setCustomCards([...customCards, newKey]);
          }
        }}
        className="text-sm text-gray-600 hover:text-gray-800 underline"
      >
        + Add custom info card
      </button>
    </div>
  );
};

const FAQEditor: React.FC<{
  details: { [key: string]: AdditionalDetail };
  onUpdate: (key: string, value: string, displayOnPage?: boolean) => void;
  getDetailValue: (key: string, defaultValue?: string) => string;
}> = ({ details, onUpdate, getDetailValue }) => {
  const faqs = [];
  for (let i = 1; i <= 5; i++) {
    const question = details[`FAQ_Q${i}`]?.value || '';
    const answer = details[`FAQ_A${i}`]?.value || '';
    if (question || answer) {
      faqs.push({ num: i, question, answer });
    }
  }

  return (
    <div className="space-y-4">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="border border-gray-200 rounded-lg p-4">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Question {i}</h5>
          <input
            type="text"
            value={getDetailValue(`FAQ_Q${i}`)}
            onChange={(e) => onUpdate(`FAQ_Q${i}`, e.target.value, true)}
            placeholder="Enter question..."
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 mb-2"
          />
          <textarea
            value={getDetailValue(`FAQ_A${i}`)}
            onChange={(e) => onUpdate(`FAQ_A${i}`, e.target.value, true)}
            placeholder="Enter answer..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 resize-none"
          />
        </div>
      ))}
    </div>
  );
};

const ContactEditor: React.FC<{
  details: { [key: string]: AdditionalDetail };
  onUpdate: (key: string, value: string, displayOnPage?: boolean) => void;
  getDetailValue: (key: string, defaultValue?: string) => string;
}> = ({ details, onUpdate, getDetailValue }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm text-gray-600">Email Address</label>
        <input
          type="email"
          value={getDetailValue('Contact_Email')}
          onChange={(e) => onUpdate('Contact_Email', e.target.value, true)}
          placeholder="contact@example.com"
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
        />
      </div>
      
      <div>
        <label className="text-sm text-gray-600">Phone Number</label>
        <input
          type="tel"
          value={getDetailValue('Contact_Phone')}
          onChange={(e) => onUpdate('Contact_Phone', e.target.value, true)}
          placeholder="+1 (555) 123-4567"
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
        />
      </div>
      
      <div>
        <label className="text-sm text-gray-600">Contact Message</label>
        <textarea
          value={getDetailValue('Contact_Message')}
          onChange={(e) => onUpdate('Contact_Message', e.target.value, true)}
          placeholder="Feel free to reach out with any questions!"
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 resize-none"
        />
      </div>
    </div>
  );
};

const AccommodationEditor: React.FC<{
  details: { [key: string]: AdditionalDetail };
  onUpdate: (key: string, value: string, displayOnPage?: boolean) => void;
  getDetailValue: (key: string, defaultValue?: string) => string;
}> = ({ details, onUpdate, getDetailValue }) => {
  return (
    <div className="space-y-4">
      {[1, 2].map(i => (
        <div key={i} className="border border-gray-200 rounded-lg p-4">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Hotel {i}</h5>
          <input
            type="text"
            value={getDetailValue(`Hotel_${i}_Name`)}
            onChange={(e) => onUpdate(`Hotel_${i}_Name`, e.target.value, true)}
            placeholder="Hotel name..."
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 mb-2"
          />
          <textarea
            value={getDetailValue(`Hotel_${i}_Details`)}
            onChange={(e) => onUpdate(`Hotel_${i}_Details`, e.target.value, true)}
            placeholder="Address, phone, special rate code..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 resize-none"
          />
        </div>
      ))}
      
      <div>
        <label className="text-sm text-gray-600">Additional Notes</label>
        <textarea
          value={getDetailValue('Accommodation_Notes')}
          onChange={(e) => onUpdate('Accommodation_Notes', e.target.value, true)}
          placeholder="Transportation info, booking deadlines, etc..."
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 resize-none"
        />
      </div>
    </div>
  );
};

const GalleryEditor: React.FC<{
  details: { [key: string]: AdditionalDetail };
  onUpdate: (key: string, value: string, displayOnPage?: boolean) => void;
  getDetailValue: (key: string, defaultValue?: string) => string;
}> = ({ details, onUpdate, getDetailValue }) => {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i}>
          <label className="text-sm text-gray-600">Image {i} URL</label>
          <input
            type="url"
            value={getDetailValue(`Gallery_URL_${i}`)}
            onChange={(e) => onUpdate(`Gallery_URL_${i}`, e.target.value, true)}
            placeholder="https://example.com/image.jpg"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
          />
        </div>
      ))}
      
      <div>
        <label className="text-sm text-gray-600">Gallery Caption</label>
        <textarea
          value={getDetailValue('Gallery_Caption')}
          onChange={(e) => onUpdate('Gallery_Caption', e.target.value, true)}
          placeholder="Our engagement photos..."
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 resize-none"
        />
      </div>
    </div>
  );
};

export default EventPageDesigner;